/**
 * Inline diff span styles (used outside the page too: review legend, inline AI preview).
 * Token-based, so they follow light/dark mode without the theme (callers may still pass it).
 * @returns {object} sx fragment.
 */
export function diffSpanSx() {
  return {
    '& .q-diff-delete': {
      backgroundColor: 'var(--q-danger-soft)',
      textDecoration: 'line-through',
      textDecorationColor: 'var(--q-danger)',
      borderRadius: '3px',
    },
    '& .q-diff-insert': {
      backgroundColor: 'var(--q-success-soft)',
      borderRadius: '3px',
    },
  };
}

/**
 * Styles for the editable page and everything rendered inside `.ProseMirror`.
 * Colors use the design tokens (`--q-*`) with fallbacks so the editor follows light/dark themes.
 *
 * @param {import('@mui/material').Theme} theme - MUI theme.
 * @param {{pageMode: 'page'|'wide', minHeight: number, compact: boolean}} opts - Layout options.
 * @returns {object} sx object for the page element.
 */
export function buildPageSx(theme, { pageMode, minHeight, compact }) {
  const page = pageMode === 'page';
  const dangerSoft = 'var(--q-danger-soft)';
  const successSoft = 'var(--q-success-soft)';
  const warningSoft = 'var(--q-warning-soft)';

  return {
    position: 'relative',
    width: '100%',
    maxWidth: compact ? 'none' : page ? 794 : 1100,
    mx: 'auto',
    boxSizing: 'border-box',
    bgcolor: 'var(--q-surface, #FFFFFF)',
    color: 'var(--q-text, #1C1B19)',
    borderRadius: compact ? 'var(--q-radius-md, 10px)' : '4px',
    border: compact ? '1px solid' : 'none',
    borderColor: 'divider',
    boxShadow: compact ? 'none' : 'var(--q-shadow-lg)',
    minHeight: compact ? minHeight : Math.max(minHeight, page ? 1123 : 600),
    transition: 'max-width 200ms ease',

    '& .ProseMirror': {
      // Body font matches the Word export default (enhancedExportService DEFAULT_EXPORT_FONT_FAMILY)
      // so the page looks like the exported file; --q-font-doc can override it.
      fontFamily: 'var(--q-font-doc, Arial, Helvetica, sans-serif)',
      color: 'var(--q-text-doc)',
      fontSize: 16,
      lineHeight: 1.6,
      minHeight: compact ? minHeight : Math.max(minHeight, page ? 1123 : 600),
      padding: compact
        ? '16px 20px 24px 36px'
        : { xs: '32px 20px 48px 32px', sm: '56px 56px 72px', md: page ? '72px 80px 96px' : '48px 64px 80px' },
      outline: 'none',
      boxSizing: 'border-box',
      overflowWrap: 'break-word',
      position: 'relative',
      caretColor: 'var(--q-accent, #4F3BD6)',
      '& :is(p, li, h1, h2, h3, h4, td, th)': { fontFamily: 'inherit' },
      '& > * + *': { marginTop: 0 },
      '& p': { margin: '0.75em 0' },
      '& h1, & h2, & h3, & h4': { fontWeight: 700, lineHeight: 1.25, margin: '1.1em 0 0.45em', letterSpacing: '-0.01em', color: 'var(--q-text)' },
      '& h1': { fontSize: '2em' },
      '& h2': { fontSize: '1.5em', fontWeight: 600 },
      '& h3': { fontSize: '1.2em', fontWeight: 600 },
      '& ul, & ol': { paddingLeft: '1.5em', margin: '0.75em 0' },
      '& li > p': { margin: '0.2em 0' },
      '& ul[data-type="taskList"]': {
        listStyle: 'none',
        paddingLeft: '0.25em',
        '& li': { display: 'flex', alignItems: 'flex-start', gap: '0.5em' },
        '& li > label': { flexShrink: 0, marginTop: '0.3em', userSelect: 'none' },
        '& li > label input': { accentColor: theme.palette.primary.main, width: 16, height: 16, cursor: 'pointer' },
        '& li > div': { flex: 1, minWidth: 0 },
        '& li[data-checked="true"] > div': { color: 'text.secondary', textDecoration: 'line-through' },
      },
      '& blockquote': {
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        paddingLeft: '1em',
        margin: '1em 0',
        color: 'var(--q-text-2, #3D3A35)',
      },
      '& code': {
        backgroundColor: theme.palette.action.hover,
        padding: '0.15em 0.35em',
        borderRadius: '4px',
        fontFamily: 'var(--q-font-mono)',
        fontSize: '0.9em',
      },
      '& pre': {
        backgroundColor: 'var(--q-subtle)',
        borderRadius: '8px',
        padding: '0.85em 1em',
        overflowX: 'auto',
        '& code': { background: 'none', padding: 0 },
      },
      '& a, & .tiptap-link': { color: 'var(--q-accent, #4F3BD6)', textDecoration: 'underline', textUnderlineOffset: '2px' },
      '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', margin: '1.5em 0' },
      // Manual page break (src/extensions/PageBreak.js): a muted dashed rule across the text
      // column with a centered "Page break" label (the label span comes from the node view).
      // Selectable like other atoms; the Pages view also ends the page there.
      '& .page-break': {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        height: 20,
        margin: '1.25em 0',
        color: 'var(--q-muted, #5E5A54)',
        cursor: 'default',
        userSelect: 'none',
        borderRadius: '4px',
        '&::before, &::after': {
          content: '""',
          flex: 1,
          minWidth: 0,
          borderTop: '1px dashed var(--q-border-strong, #D3CFC7)',
        },
        '& .page-break-label': {
          flexShrink: 0,
          fontFamily: 'var(--q-font-sans, inherit)',
          fontSize: 11.5,
          fontWeight: 500,
          lineHeight: 1,
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
        },
        '&.ProseMirror-selectednode': {
          outline: '2px solid var(--q-accent, #4F3BD6)',
          outlineOffset: '2px',
        },
      },
      // Pages view (src/extensions/Pagination.js): the page already ends at a break, so the
      // node collapses to zero height like in print (it can never spill into the bottom margin);
      // the rest of that page shows a muted "Page break" label instead. Selected, it shows as a
      // thin accent line so Backspace / Delete still read clearly.
      '&.q-paginated .page-break': {
        height: 0,
        margin: 0,
        padding: 0,
        overflow: 'visible',
        '&::before, &::after, & .page-break-label': { display: 'none' },
        '&.ProseMirror-selectednode': { outline: 'none', boxShadow: '0 0 0 1px var(--q-accent, #4F3BD6)' },
      },
      '& table': { borderCollapse: 'collapse', margin: '1em 0', width: '100%', tableLayout: 'fixed' },
      '& td, & th': {
        border: '1px solid var(--q-border, #E3E0DA)',
        padding: '8px 12px',
        textAlign: 'left',
        verticalAlign: 'top',
        position: 'relative',
      },
      '& th': { backgroundColor: 'var(--q-subtle, #F1EFEB)', fontWeight: 600 },
      '& .selectedCell::after': { background: 'var(--q-focus-halo)' },
      '& *:has(> img.tiptap-image)': { pointerEvents: 'auto' },
      '& img.tiptap-image': {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '4px',
        display: 'block',
        margin: '16px 0',
        pointerEvents: 'auto',
        '&.ProseMirror-selectednode': { outline: `2px solid ${theme.palette.primary.main}` },
      },
      '& img[data-align="left"]': { marginLeft: 0, marginRight: 'auto' },
      '& img[data-align="center"]': { marginLeft: 'auto', marginRight: 'auto' },
      '& img[data-align="right"]': { marginLeft: 'auto', marginRight: 0 },
      '& [data-mermaid-block]': { margin: '16px 0' },

      // Placeholder (TipTap Placeholder extension)
      '& .is-empty::before': {
        content: 'attr(data-placeholder)',
        float: 'left',
        height: 0,
        pointerEvents: 'none',
        color: 'var(--q-muted, #77726B)',
        opacity: 0.8,
      },

      // AI review
      '& .q-diff-delete': {
        backgroundColor: dangerSoft,
        textDecoration: 'line-through',
        textDecorationColor: 'var(--q-danger, #D63B1F)',
        borderRadius: '3px',
      },
      '& .q-diff-insert': { backgroundColor: successSoft, borderRadius: '3px' },
      '& [data-diff-block="delete"]': {
        backgroundColor: dangerSoft,
        textDecoration: 'line-through',
        textDecorationColor: 'var(--q-danger, #D63B1F)',
        boxShadow: 'inset 3px 0 0 var(--q-danger, #D63B1F)',
        borderRadius: '4px',
        opacity: 0.8,
      },
      // In Pages view a deleted section or list can hold a page end (a Pagination widget). An
      // opacity on the block would fade the gap between the two sheets too (a child cannot undo
      // it), so the fade moves down to the parts around the widget. Without :has support the
      // plain rule above applies.
      '& [data-diff-block="delete"]:has([data-q-pagination])': { opacity: 1 },
      '& [data-diff-block="delete"]:has([data-q-pagination]) > :not([data-q-pagination]):not(:has([data-q-pagination]))': {
        opacity: 0.8,
      },
      '& [data-diff-block="delete"]:has([data-q-pagination]) :has([data-q-pagination]) > :not([data-q-pagination]):not(:has([data-q-pagination]))': {
        opacity: 0.8,
      },
      '& [data-diff-block="insert"]': {
        backgroundColor: successSoft,
        boxShadow: 'inset 3px 0 0 var(--q-success)',
        borderRadius: '4px',
      },
      '& .q-review-active': { outline: '2px solid var(--q-accent, #4F3BD6)', outlineOffset: '1px' },
      '& .q-block-flash': { animation: 'qBlockFlash 1.2s ease-out' },
      '@keyframes qBlockFlash': {
        '0%': { backgroundColor: 'var(--q-selection)' },
        '100%': { backgroundColor: 'transparent' },
      },

      // Find & replace
      '& .q-search-match': { backgroundColor: warningSoft, borderRadius: '2px' },
      '& .q-search-match-active': { backgroundColor: '#F2B705', color: '#1C1B19' },

      // Templates
      '& .q-tpl-variable': {
        display: 'inline',
        padding: '1px 6px',
        borderRadius: '6px',
        backgroundColor: 'var(--q-subtle, #F1EFEB)',
        border: '1px solid var(--q-border, #E3E0DA)',
        fontSize: '0.86em',
        fontWeight: 500,
        color: 'var(--q-text-2, #3D3A35)',
        whiteSpace: 'nowrap',
      },
      '& [data-tpl-locked="true"]': {
        position: 'relative',
        boxShadow: 'inset 3px 0 0 var(--q-border-strong, #D3CFC7)',
        paddingLeft: '10px',
        '&::after': {
          content: '"Locked"',
          position: 'absolute',
          top: 0,
          right: 0,
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--q-muted, #77726B)',
          fontStyle: 'normal',
          textDecoration: 'none',
          pointerEvents: 'none',
        },
      },
      '& tr[data-tpl-repeat]': { outline: '1px dashed var(--q-accent-muted)', outlineOffset: '-1px' },
      // AI field node views draw their own card/chip; the wrapper must not add a second
      // border and tint (index.css styles bare `[data-tpl-field]` elements for static HTML).
      '& .q-tpl-field-block, & .q-tpl-field-inline': {
        border: 0,
        padding: 0,
        margin: 0,
        background: 'none',
        borderRadius: 0,
        color: 'inherit',
      },
      // Heading size of the AI field chip: same border, tint, icon and radius, scaled to the heading.
      '& :is(h1, h2, h3, h4) .q-tpl-chip': {
        fontSize: 'max(0.6em, 15px)',
        fontWeight: 600,
        letterSpacing: 0,
        padding: '2px 8px',
        maxWidth: '100%',
        verticalAlign: 'middle',
      },

      // Generated-document fields
      // No box at rest or on hover: GeneratedSectionChrome draws the dashed outline and actions.
      '& .q-generated-field': { borderRadius: '6px' },
      '& [data-field-status="needs_input"]': {
        backgroundColor: warningSoft,
        borderRadius: '4px',
      },
    },
  };
}
